/**
 * The shape of an ERP table response, and the URL that asks for it.
 *
 * Deliberately free of imports. `erpClient.ts` pulls in MSAL for the token,
 * and MSAL's config throws at import time when the VITE_* env vars are absent,
 * which makes anything importing it untestable. Same split the module already
 * uses for crm-igcom, where the tests cover insights.ts and storedOverview.ts
 * rather than igcomClient.ts -- and the same split as the server side, where
 * erpOrdersQuery.ts is tested and erpDb.ts is not.
 */

/** One ERP row, keyed by display column name. Values are TEXT in their
 * schema — including the dates and the quantities — so they arrive as
 * strings, and anything that needs arithmetic has to convert deliberately
 * rather than trusting the type. A boolean only ever arrives for a registry
 * FLAG (Order Monitoring's duplicate-Order-No. count), never for a column of
 * one of their tables. */
export type ErpTableRow = Record<string, string | number | boolean | null>;

export interface ErpTablePage {
  /** The registry key that was read. */
  table: string;
  asOf: string;
  page: number;
  pageSize: number;
  maxPageSize: number;
  total: number;
  totalPages: number;
  search: string;
  /** The display names the search was restricted to, after the allow-list. */
  searchIn?: string[];
  /** The header-dropdown filters the server actually applied. */
  filters?: Record<string, string[]>;
  /** The column the server actually sorted on, after its allow-list. */
  sort: string;
  dir: "ASC" | "DESC";
  columns: string[];
  /**
   * Which of their write buttons this screen has, from the server's write
   * registry — never a second list kept here, which would drift.
   */
  writes?: {
    add: boolean;
    edit: boolean;
    delete: boolean;
    /** Why ADD is off, when it is off for a reason worth showing. */
    addRefused?: string;
    /** IMPORT additionally needs the screen's import columns to be its own. */
    import: boolean;
    importRefused?: string;
    /**
     * Why this account cannot write at all — writes switched off, or not on
     * the allow-list. Absent when it can. Answered by the SAME function the
     * write endpoint gates on, asked in advance.
     */
    blocked?: string;
  };
  rows: ErpTableRow[];
  /** Inbound: "Order No. X matched 2 MFG No(s)" when the search term was an identifier. */
  traceSearchNote?: string;
}

export interface FetchErpTableParams {
  /** A key in the server's registry — their Flask endpoint name. */
  table: string;
  /** 1-based, matching the API. The shared Pagination control is 0-based, so
   * the screen adds one — the two conventions meet in exactly one place. */
  page?: number;
  pageSize?: number;
  search?: string;
  /** Display names to restrict the search to — Order Monitoring's quick
   * searches name their own field. Allow-listed server-side. */
  searchIn?: readonly string[];
  /** A display name from `ErpOrdersPage.columns`; anything else is ignored
   * server-side rather than trusted. */
  sort?: string;
  dir?: "ASC" | "DESC";
  /**
   * Their header dropdowns: display name -> the ticked values. Sent as
   * repeated `f_<display name>` parameters, which is how several values for
   * one column survive a query string. Allow-listed server-side.
   */
  filters?: Readonly<Record<string, readonly string[]>>;
  /**
   * The same, matched case- and padding-insensitively — sent as `ff_<display
   * name>`. This is how their PER-DOCUMENT lookups match (GENERATE DS reads
   * `WHERE UPPER(TRIM(ds_no)) = ?`), and it is deliberately not how the
   * header dropdowns match: one asks for a document, the other for the rows
   * holding a value somebody ticked.
   */
  foldedFilters?: Readonly<Record<string, readonly string[]>>;
  /** Forecast only: daily / weekly / monthly / yearly, and the YYYY-MM-DD it is around. */
  period?: string;
  date?: string;
}

/** One column's ▾ dropdown list, from /api/erp/table/values. */
export interface ErpColumnValues {
  table: string;
  column: string;
  values: string[];
  /**
   * The column has more distinct values than a dropdown is worth — their
   * FILTERABLE_MAX_DISTINCT. `values` is empty when this is true, and their
   * own template draws no caret at all for such a column.
   */
  tooMany: boolean;
  limit: number;
}

/**
 * Build the request URL.
 *
 * Split out from the fetch so it can be tested without a network or a token.
 * Defaults are omitted rather than sent, which keeps the URL readable in the
 * network tab and means the server's defaults stay the single definition of
 * what "no page size given" means.
 */
export function buildErpTableUrl(params: FetchErpTableParams): string {
  const q = new URLSearchParams();
  q.set("table", params.table);
  if (params.page !== undefined && params.page > 1) q.set("page", String(params.page));
  if (params.pageSize !== undefined) q.set("pageSize", String(params.pageSize));
  const search = (params.search ?? "").trim();
  if (search) q.set("search", search);
  if (search && params.searchIn && params.searchIn.length > 0) {
    q.set("searchIn", params.searchIn.join(","));
  }
  if (params.sort) q.set("sort", params.sort);
  if (params.dir) q.set("dir", params.dir);
  if (params.period) q.set("period", params.period);
  if (params.date) q.set("date", params.date);
  // Sorted by column name, and values in the order they were given: a URL
  // that changes only because a Map iterated differently would miss the
  // browser's cache and re-read the table for the same question.
  for (const display of Object.keys(params.filters ?? {}).sort()) {
    for (const value of params.filters![display]) {
      if (value === "") continue;
      q.append(`f_${display}`, value);
    }
  }
  for (const display of Object.keys(params.foldedFilters ?? {}).sort()) {
    for (const value of params.foldedFilters![display]) {
      if (value === "") continue;
      q.append(`ff_${display}`, value);
    }
  }
  const qs = q.toString();
  // `table` is always present, so there is always a query string.
  return `/api/erp/table?${qs}`;
}

/** The URL for one column's ▾ dropdown values. */
export function buildErpColumnValuesUrl(
  table: string, column: string, extra?: Readonly<Record<string, string>>
): string {
  const q = new URLSearchParams();
  q.set("table", table);
  q.set("column", column);
  // Forecast's period and date: its dropdown lists the values of THAT period.
  for (const k of Object.keys(extra ?? {}).sort()) q.set(k, extra![k]);
  return `/api/erp/table/values?${q.toString()}`;
}
